import React, { useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import LayoutContainer from 'components/Common/LayoutContainer'
import { mediaQueryUp } from 'components/utils/breakpoint'

import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import {
  getCheckoutDate,
  getDurationPackageOptions,
  getLowestAvailableStayPayTier,
  OfferPackageOptionView,
} from 'selectors/offerPage/offerPageSelectors'
import Caption from 'components/Luxkit/Typography/Caption'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { OfferPageRoomOption } from 'contexts/OfferPage/offerPageStateReducer'
import config from 'constants/config'
import useOfferAvailableRates from 'hooks/useOfferAvailableRates'
import { initialRooms } from 'lib/offer/occupancyUtils'
import ContactContext from 'contexts/contactContext'
import LineInfoCircleIcon from 'components/Luxkit/Icons/line/LineInfoCircleIcon'
import Plural from 'components/utils/Formatters/Plural'
import usePropertyOfferMapping from 'hooks/Offers/usePropertyOfferMapping'
import useOffer from 'hooks/Offers/useOffer'
import PropertyRoomCheapestOptionMobile from './PropertyRoomCheapestOptionMobile'
import usePropertyBedbankViews from 'hooks/OfferPage/usePropertyBedbankViews'
import Pane from 'components/Common/Pane'
import Heading from 'components/Luxkit/Typography/Heading'
import { getPackageOptionsByRoom } from '../singlePropertyUtils'
import SinglePropertyHotelFlightsToggleButton from 'components/OfferPage/Accommodation/HotelFlightsToggle/SinglePropertyHotelFlightsToggleButton'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'

const PackagesLayoutContainer = styled(LayoutContainer)`
  padding: 0;

  ${mediaQueryUp.desktop} {
    padding: 0 ${rem(16)};
  }
`

interface Props {
  offer: App.HotelOffer;
  rooms?: Array<App.Occupants>;
  checkIn?: string;
  checkOut?: string;
  packageOptions: Array<OfferPackageOptionView>;
  roomOption?: OfferPageRoomOption;
  showFlightInformation?: boolean;
  lowestAvailableStayPayTier?: App.StayPayTier | null;
}

function PropertyRoomOptionsMobile(props: Props) {
  const {
    offer,
    packageOptions,
    showFlightInformation,
    roomOption,
    checkIn,
    checkOut,
    rooms = initialRooms,
    lowestAvailableStayPayTier,
  } = props

  const supportContact = useContext(ContactContext)
  const hasRoomOption = roomOption && Object.entries(roomOption).length === 2

  const [bedbankPackageViews, fetchingBedbankViews] = usePropertyBedbankViews(offer.property.id)
  const [mapping] = usePropertyOfferMapping(offer.property.id)
  const [bedbankOffer] = useOffer<App.BedbankOffer>(mapping?.bedbankOfferId)

  // Get the mapping for this property page so we know what bedbank offer to fetch
  const packageOptionsByRoom = useMemo(() => getPackageOptionsByRoom(packageOptions, bedbankPackageViews, mapping), [packageOptions, bedbankPackageViews, mapping])

  const offerAvailableRates = useOfferAvailableRates(offer?.id, {
    checkIn,
    checkOut,
    rooms,
  })

  const hasRateError = !!offerAvailableRates?.error

  return (<VerticalSpacer gap={16}>
    <LayoutContainer>
      <VerticalSpacer gap={16}>
        <Heading variant="heading4">Room Options</Heading>
        <SinglePropertyHotelFlightsToggleButton />
        {hasRateError && <MessageBanner
          kind="warning"
          tabletAlign="center"
          heading={`The third party booking system for ${offer.property?.name} is currently down`}
          description={`Please call our VIP team on ${supportContact.local.humanReadable} who are ready to provide assistance to complete your booking`}
        />}
      </VerticalSpacer>
    </LayoutContainer>

    {!hasRateError && <PackagesLayoutContainer>
      <VerticalSpacer gap={32}>
        {!checkIn && lowestAvailableStayPayTier && <MessageBanner
          kind="promotional-primary"
          icon={<LineInfoCircleIcon />}
          description={<>
            <b>Special offer:</b> Enjoy free nights for stays longer than <Plural count={lowestAvailableStayPayTier.stayNights} singular="day" withCount />. Select dates to check eligibility.
          </>}
        />}
        <Pane background="neutral-six">
          <VerticalSpacer gap={8}>
            {packageOptionsByRoom.map((item) => {
              if (hasRoomOption && roomOption.roomTypeId === item.roomId) {
                return null
              }
              return (<PropertyRoomCheapestOptionMobile
                key={item.roomId}
                offer={offer}
                packageViews={item.lpc}
                bedbankViews={item.bedbank}
                bedbankOffer={bedbankOffer}
                fetchingBedbankViews={fetchingBedbankViews}
                detailsModalMode="room-and-package"
              />)
            })}
          </VerticalSpacer>
        </Pane>
      </VerticalSpacer>
    </PackagesLayoutContainer>}
    {showFlightInformation && <LayoutContainer>
      <Caption variant="medium" align="center">
        *Price is indicative and based on sample return fares (inclusive of booking fees on Low Cost Carriers). To view live pricing, conduct a full search. {config.brandConfig.name} full Terms and Conditions apply.
      </Caption>
    </LayoutContainer>}
  </VerticalSpacer>
  )
}

type MappedProps = Pick<Props,
'offer' |
'checkIn' |
'checkOut' |
'rooms' |
'roomOption' |
'packageOptions' |
'lowestAvailableStayPayTier'
> & { open?: boolean }

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  offer: state.offer as App.HotelOffer,
  checkIn: state.checkInDate,
  checkOut: getCheckoutDate(state),
  rooms: state.rooms,
  roomOption: state.roomOption,
  packageOptions: getDurationPackageOptions(state, appState),
  lowestAvailableStayPayTier: getLowestAvailableStayPayTier(state, appState),
  open: !!state.activeFilter,
}), PropertyRoomOptionsMobile, preventRenderWithOpenFilter)
